import { createUrl, stripTags, timeMask } from 'app/utils';
import { Box, ButtonIcon, ConditionalLink, Flex, Typography } from 'components';
import { ContentsItem, contentsTypes, ContentTypeEnum } from 'features/contents';
import { ProductTypeEnum } from 'features/products';
import React from 'react';
import styled from 'styled-components';

const LessonBullet = styled('i')`
  position: absolute;
  z-index: 5;
  width: 12px;
  height: 22px;
  border-radius: 11px;
  top: 12px;
  left: 15px;

  &::before {
    content: '';
    border-radius: 50%;
    border: 2px solid;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
  }
`;

const FlexBorder = styled(Flex)``;

const CustomConditionalLink = styled(ConditionalLink)`
  display: flex;
  position: relative;
  background: ${(props: any) => (props['data-current'] ? props.theme.colors.gray_600 : 'transparent')};

  &::before {
    content: '';
    display: block;
    width: 2px;
    height: 100%;
    background: ${(props: any) => (props['data-current'] ? props.theme.colors.white25 : props.theme.colors.gray_600)};
    position: absolute;
    top: 0;
    left: 20px;
  }

  ${FlexBorder} {
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray_600};
  }

  &:last-child {
    border-radius: 4px;

    ${FlexBorder} {
      border-bottom: 0;
    }

    &:before {
      height: 50%;
    }
  }

  ${LessonBullet} {
    background: ${(props: any) => (props['data-current'] ? props.theme.colors.gray_600 : props.theme.colors.dark_150)};

    &::before {
      border-color: ${(props: any) =>
        props['data-active'] ? props.theme.colors.primary_400 : props['data-current'] ? props.theme.colors.white25 : props.theme.colors.gray_600};
      background: ${(props: any) => (props['data-active'] ? props.theme.colors.primary_400 : 'transparent')};
    }
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray_600};

    &::before {
      background: ${({ theme }) => theme.colors.white25};
    }

    ${LessonBullet} {
      background: ${({ theme }) => theme.colors.gray_600};

      &::before {
        border-color: ${(props: any) => (props['data-active'] ? props.theme.colors.primary_400 : props.theme.colors.gray_500)};
      }
    }
  }
`;

interface ContentCardProps {
  content: ContentsItem;
  index: number;
  classUuid: string;
  productSlug: string;
  moduleUuid?: string;
  isCurrent: boolean;
  showPercentage?: boolean;
  productType: ProductTypeEnum;
}

const ContentCard = ({ content, index, productSlug, classUuid, moduleUuid, isCurrent, showPercentage = true }: ContentCardProps) => {
  return (
    <CustomConditionalLink
      key={`content-${content.uuid}-${index.toString()}`}
      to={
        moduleUuid && classUuid
          ? moduleUuid
            ? classUuid
              ? createUrl('contentClassDetails', [
                  productSlug,
                  classUuid,
                  moduleUuid,
                  content.uuid,
                  contentsTypes(content.contentType, content.externalDocumentType),
                ])
              : createUrl('contentDetails', [productSlug, moduleUuid, content.uuid, contentsTypes(content.contentType, content.externalDocumentType)])
            : createUrl('productContentClassDetails', [
                productSlug,
                classUuid,
                '',
                content.uuid,
                contentsTypes(content.contentType, content.externalDocumentType),
              ])
          : createUrl('productNoClassContent', [
              productSlug,
              classUuid,
              moduleUuid,
              content.uuid,
              contentsTypes(content.contentType, content.externalDocumentType),
            ])
      }
      type={Boolean(content.uuid) ? 'internal' : 'nolink'}
      component={Flex}
      data-active={content.isComplete}
      data-current={isCurrent}
      replace
    >
      <Flex flexWrap="nowrap" alignItems="flex-start" position="relative" pl={40}>
        <FlexBorder py={3}>
          <LessonBullet />

          <Flex flexWrap="nowrap">
            {showPercentage && (
              <Flex flexGrow={0} flexShrink={0} width={45}>
                <Typography type="type1" color={Boolean(content.uuid) ? 'primary_400' : 'black30'} fontWeight="bold">
                  {content.isOptional ? '' : `${content.completionPercentage}%`}
                </Typography>
              </Flex>
            )}

            <Flex pr={3}>
              <Typography type="type1" fontWeight="bold" color={content.isOptional ? 'gray_600' : 'white'}>
                {stripTags(content.name)}
              </Typography>
            </Flex>
          </Flex>
        </FlexBorder>
      </Flex>

      <FlexBorder flexGrow={0} pr={20} alignItems="center" flexWrap="nowrap">
        {content.contentType === ContentTypeEnum.Video && (
          <>
            <Box display={{ m: 'none', d: 'inline' }} mr="10px">
              {Boolean(content.uuid) && (
                <Typography type="type0" color="gray_500" style={{ whiteSpace: 'nowrap' }}>
                  {timeMask(content.durationSeconds)}
                </Typography>
              )}
            </Box>

            <ButtonIcon
              icon="play"
              width={{ m: 20 }}
              height={{ m: 20 }}
              bg="transparent"
              color={Boolean(content.uuid) ? (content.isComplete ? 'white25' : 'primary_400') : 'white25'}
              title="Assistir Vídeo"
            />
          </>
        )}

        {content.contentType === ContentTypeEnum.Article && (
          <>
            <ButtonIcon
              icon="text"
              width={{ m: 20 }}
              height={{ m: 20 }}
              bg="transparent"
              color={Boolean(content.uuid) ? (content.isComplete ? 'white25' : 'primary_400') : 'white25'}
              title="Ler Texto"
            />
          </>
        )}

        {content.contentType === ContentTypeEnum.Quiz && (
          <>
            <Box display={{ m: 'none', d: 'inline' }}>
              <Typography type="type0" color="gray_500" mr="10px" style={{ whiteSpace: 'nowrap' }}>
                {content.numberOfQuestions} questões
              </Typography>
            </Box>

            <ButtonIcon
              icon="quiz"
              width={{ m: 20 }}
              height={{ m: 20 }}
              bg="transparent"
              color={Boolean(content.uuid) ? (content.isComplete ? 'white25' : 'primary_400') : 'white25'}
              title="Responder Quiz"
            />
          </>
        )}

        {content.contentType === ContentTypeEnum.Survey && (
          <>
            <ButtonIcon
              icon="commentQuestionOutline"
              width={{ m: 20 }}
              height={{ m: 20 }}
              bg="transparent"
              color={Boolean(content.uuid) ? (content.isComplete ? 'white25' : 'primary_400') : 'white25'}
              title="Responder pesquisa"
            />
          </>
        )}
      </FlexBorder>
    </CustomConditionalLink>
  );
};

export default ContentCard;
